import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import {
  orderedCategories,
  entriesByCategory,
  glossaryEntries,
  type HelpEntry,
  type CategoryMeta,
} from "@/lib/help-content";

/** Scroll to anchor on load or hash change */
function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          100
        );
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);
}

export function Guide() {
  useSetPageHeader({
    title: "User Guide",
    subtitle: "How to use the dashboards and what the numbers mean",
    icon: BookOpen,
  });

  useScrollToHash();
  const [search, setSearch] = useState("");
  const categories = orderedCategories();

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return glossaryEntries().filter(
      (e) =>
        e.term.toLowerCase().includes(q) ||
        e.brief.toLowerCase().includes(q) ||
        e.full.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <div className="mx-auto max-w-5xl pb-20">
      {/* Search */}
      <input
        type="text"
        placeholder="Search terms..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />

      {filteredEntries ? (
        /* Search results */
        <SearchResults entries={filteredEntries} onClear={() => setSearch("")} />
      ) : (
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
          {/* Sticky TOC sidebar — desktop only */}
          <nav className="hidden lg:block">
            <div className="sticky top-20 rounded-xl border border-border bg-card p-4">
              <h2 className="mb-3 text-xs font-semibold text-card-foreground uppercase tracking-wide">
                Contents
              </h2>
              <ul className="text-sm space-y-1.5">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={`#${cat.anchor}`}
                      className="text-muted-foreground hover:text-primary transition-colors text-xs"
                    >
                      {cat.title}
                    </a>
                  </li>
                ))}
                <li>
                  <a href="#glossary" className="text-muted-foreground hover:text-primary transition-colors text-xs">
                    Quick Glossary
                  </a>
                </li>
              </ul>
            </div>
          </nav>

          {/* Mobile TOC — inline card */}
          <nav className="lg:hidden mb-8 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3 text-sm font-semibold text-card-foreground uppercase tracking-wide">
              Contents
            </h2>
            <ul className="columns-2 gap-x-6 text-sm space-y-1">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={`#${cat.anchor}`}
                    className="text-primary hover:underline"
                  >
                    {cat.title}
                  </a>
                </li>
              ))}
              <li>
                <a href="#glossary" className="text-primary hover:underline">
                  Quick Glossary
                </a>
              </li>
            </ul>
          </nav>

          {/* Main content */}
          <div className="space-y-10">
            {/* Category sections */}
            {categories.map((cat) => (
              <CategorySection key={cat.id} category={cat} />
            ))}

            {/* Glossary */}
            <section id="glossary" className="scroll-mt-8 space-y-3">
              <h2 className="text-lg font-bold text-foreground border-b border-border pb-2">
                Quick Glossary
              </h2>
              <div className="divide-y divide-border">
                {glossaryEntries().map((entry) => (
                  <div
                    key={entry.anchor}
                    id={`glossary-${entry.anchor}`}
                    className="py-3 scroll-mt-20"
                  >
                    <p className="font-semibold text-foreground">{entry.term}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {entry.brief}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helper components ── */

function CategorySection({ category }: { category: CategoryMeta }) {
  const entries = entriesByCategory(category.id);

  return (
    <section id={category.anchor} className="scroll-mt-8 space-y-3">
      <h2 className="text-lg font-bold text-foreground border-b border-border pb-2">
        {category.title}
      </h2>
      <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
        {category.intro && <p>{category.intro}</p>}
        {category.guideExtra?.()}

        {entries.map((entry) => (
          <EntryBlock key={entry.anchor} entry={entry} />
        ))}
      </div>
    </section>
  );
}

function EntryBlock({ entry }: { entry: HelpEntry }) {
  const paragraphs = entry.full.split("\n\n");

  return (
    <div id={entry.anchor} className="scroll-mt-20">
      <p className="font-semibold text-foreground">{entry.term}</p>
      {paragraphs.map((para, i) => (
        <p key={i} className="mt-1">
          {para}
        </p>
      ))}
      {entry.guideExtra?.()}
    </div>
  );
}

function SearchResults({
  entries,
  onClear,
}: {
  entries: HelpEntry[];
  onClear: () => void;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No matching terms found.</p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {entries.length} result{entries.length !== 1 ? "s" : ""}
      </p>
      {entries.map((entry) => (
        <div
          key={entry.anchor}
          className="rounded-lg border border-border bg-card p-4"
        >
          <p className="font-semibold text-foreground">{entry.term}</p>
          <p className="mt-1 text-xs text-muted-foreground">{entry.brief}</p>
          <button
            type="button"
            onClick={() => {
              onClear();
              setTimeout(() => {
                const el = document.getElementById(entry.anchor);
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 100);
            }}
            className="mt-2 text-xs text-primary hover:underline"
          >
            View in guide &rarr;
          </button>
        </div>
      ))}
    </div>
  );
}
